import { Component, ChangeDetectionStrategy, inject } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { selectStaffUsers } from 'src/app/store/users'
import { GenericOption } from 'src/app/store/accounts/account.selectors'
import { map, take } from 'rxjs/operators'
import { compareBy } from 'src/services/table-utils/compare'
import { AuthApiService } from 'src/api/auth'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import {
  BuyersGroup,
  BuyersGroupApiService,
  ShareBuyersGroupSkipped,
} from '../buyers-groups-item/buyers-groups.service'

const shareTargetRequired = (isPublic: boolean) => (group: AbstractControl): ValidationErrors | null => {
  const { users, makePublic } = group.value as { users?: string[]; makePublic?: boolean }
  return makePublic !== isPublic || users?.length ? null : { shareTargetRequired: true }
}

@Component({
  selector: 'tc-share-buyers-group',
  templateUrl: './share-buyers-group.component.html',
  styleUrls: ['./share-buyers-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ShareBuyersGroupComponent {
  private formBuilder = inject(FormBuilder)
  private dialogRef = inject<MatDialogRef<ShareBuyersGroupComponent>>(MatDialogRef)
  private store = inject(Store)
  private toaster = inject(ToasterService)
  private buyersGroupService = inject(BuyersGroupApiService)
  private AuthApi = inject(AuthApiService)

  formGroup: FormGroup
  users$: Observable<GenericOption[]>
  saving = false
  group: BuyersGroup

  constructor() {
    const dialogData = inject<{
    group: BuyersGroup;
}>(MAT_DIALOG_DATA)

    this.group = dialogData.group
    const isPublic = !!this.group.is_public
    this.formGroup = this.formBuilder.group(
      {
        users: [{ value: [], disabled: isPublic }],
        makePublic: [isPublic],
      },
      { validators: shareTargetRequired(isPublic) },
    )

    const usersCtrl = this.formGroup.controls.users
    this.formGroup.controls.makePublic.valueChanges.subscribe((makePublic: boolean) => {
      if (makePublic) usersCtrl.disable({ emitEvent: false })
      else usersCtrl.enable({ emitEvent: false })
    })

    const currentUserId = this.AuthApi.currentUser.user_id
    this.users$ = this.store.select(selectStaffUsers).pipe(
      map(users => users
        .filter(user => !user.archived && user.user_id !== currentUserId)
        .sort(compareBy('fullname'))
        .map(user => ({ id: user.user_id, name: user.fullname, hint: user.primaryemail }))
      ),
    )
  }

  cancel() {
    this.dialogRef.close()
  }

  private async formatSkipped(skipped: ShareBuyersGroupSkipped[]): Promise<string> {
    const MAX = 5
    if (skipped.length <= MAX) {
      const users = await this.users$.pipe(take(1)).toPromise()
      const nameById = new Map(users.map((u) => [u.id, u.name] as const))
      return skipped.map((s) => `${nameById.get(s.user_id) ?? s.user_id}: ${s.reason}`).join('\n')
    }
    const grouped = skipped.reduce<Record<string, number>>((acc, s) => {
      acc[s.reason] = (acc[s.reason] ?? 0) + 1
      return acc
    }, {})
    return Object.entries(grouped)
      .map(([reason, count]) => `${count} × ${reason}`)
      .join('; ')
  }

  async save() {
    const { users = [], makePublic } = this.formGroup.value as { users?: string[]; makePublic: boolean }
    const { group } = this
    const togglePublic = makePublic !== !!group.is_public
    const publish = togglePublic && makePublic
    const unpublish = togglePublic && !makePublic

    this.saving = true
    try {
      if (unpublish) {
        await this.buyersGroupService.setPublic(group.group_id, false)
        this.toaster.success(`Buyer Group "${group.name}" is no longer public.`)
        if (!users.length) {
          this.dialogRef.close({ is_public: false })
          return
        }
      }

      const response = publish
        ? await this.buyersGroupService.setPublic(group.group_id, true)
        : await this.buyersGroupService.share({ group_id: group.group_id, target_user_ids: users })
      const { fulfilled = [], skipped = [] } = response?.data ?? {}
      const action = publish ? 'made public and shared' : 'shared'

      if (fulfilled.length && !skipped.length) {
        this.toaster.success(`Buyer Group "${group.name}" was ${action} with ${fulfilled.length} user(s).`)
      } else if (fulfilled.length && skipped.length) {
        this.toaster.warning(
          `Buyer Group "${group.name}" was ${action} with ${fulfilled.length} user(s); ${skipped.length} skipped.`,
          await this.formatSkipped(skipped),
        )
      } else if (publish && skipped.length) {
        this.toaster.warning(
          `Buyer Group "${group.name}" is now public; ${skipped.length} user(s) skipped.`,
          await this.formatSkipped(skipped),
        )
      } else if (publish) {
        this.toaster.warning(`Buyer Group "${group.name}" is now public but was not shared with anyone.`)
      } else if (skipped.length) {
        this.toaster.error(
          `Buyer Group "${group.name}" was not shared. ${skipped.length} user(s) skipped.`,
          await this.formatSkipped(skipped),
        )
        if (!unpublish) return
      } else {
        this.toaster.warning(`Buyer Group "${group.name}" was not shared with anyone.`)
        if (!unpublish) return
      }

      this.dialogRef.close({ ...this.formGroup.value, is_public: makePublic, fulfilled, skipped })
    } catch (err) {
      this.toaster.error(`Unable to share Buyer Group "${group.name}".`, err)
    } finally {
      this.saving = false
    }
  }
}